import { useState } from "react";
import API, { formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Lock, Mail, ArrowRight } from "lucide-react";
import CombinedLogo from "@/components/CombinedLogo";

const TAGNPAY_LOGO = "https://customer-assets.emergentagent.com/job_images-34/artifacts/tdji2o49_tagnpay-logo-new.png";
const TRANSASIA_LOGO = "https://customer-assets.emergentagent.com/job_ac147b83-bb9e-4da1-b506-44afecc6b2ce/artifacts/4lmb9h3d_IMG-20260413-WA0117.jpg";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await API.post("/auth/admin/login", { email, password });
      login(data.user, data.access_token);
      navigate("/admin");
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB] px-4 relative overflow-hidden" data-testid="admin-login-page">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #008A99 1px, transparent 0)', backgroundSize: '32px 32px' }} />

      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4"><CombinedLogo /></div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Admin Portal</h1>
          <p className="text-sm text-gray-500 mt-1.5">MyErba Loyalty Management</p>
          <div className="flex items-center justify-center gap-2 mt-2 opacity-50">
            <span className="text-[9px] uppercase tracking-[0.15em] text-gray-500 font-medium">Powered by</span>
            <img src={TAGNPAY_LOGO} alt="TagNPay" className="h-3.5" />
          </div>
        </div>

        {error && <div className="mb-5 p-3.5 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm font-medium" data-testid="admin-login-error">{error}</div>}

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-7 space-y-5">
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Email</label>
            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input data-testid="admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@transasia.com" className="pl-10 h-12 rounded-xl border-gray-200 focus:border-[#008A99] focus:ring-[#008A99]" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Password</label>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter password" className="pl-10 h-12 rounded-xl border-gray-200 focus:border-[#008A99] focus:ring-[#008A99]" />
            </div>
          </div>
          <Button data-testid="admin-login-button" type="submit" disabled={loading} className="w-full h-12 rounded-xl bg-[#008A99] hover:bg-[#006670] text-white font-semibold transition-all duration-200 shadow-lg shadow-[#008A99]/20 disabled:shadow-none">
            {loading ? "Signing in..." : "Sign In"} {!loading && <ArrowRight className="ml-2 w-4 h-4" />}
          </Button>
        </form>
        <div className="text-center mt-5">
          <a href="/login" className="text-sm text-gray-400 hover:text-[#008A99] font-medium transition-colors" data-testid="customer-login-link">Customer Login</a>
        </div>
      </div>
    </div>
  );
}
